// src/components/LoginForm.js
import React, { useState, useEffect } from 'react';
import './AuthForms.css';

const LoginForm = ({ onSuccess, appName, logoUrl, switchToSignup, switchToForgot }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(30);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0 && unconfirmed) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown, unconfirmed]);

  useEffect(() => {
    if (unconfirmed && resendCooldown === 30) {
      (async () => {
        try {
          const res = await fetch(process.env.REACT_APP_RESEND_CONFIRMATION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error);
          console.log('✅ Confirmation code auto-sent after login');
        } catch (err) {
          console.error('❌ Auto resend failed:', err.message);
        }
      })();
    }
  }, [unconfirmed, resendCooldown, email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginRes = await fetch(process.env.REACT_APP_LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const loginData = await loginRes.json();
      if (!loginRes.ok) {
        if (loginData.error?.includes('User is not confirmed')) {
          setUnconfirmed(true);
          return;
        }
        throw new Error(loginData.error || 'Login failed');
      }

      const token = loginData.tokens?.IdToken || loginData.tokens?.AccessToken;
      if (!token) throw new Error('No token returned from login');

      const confirmRes = await fetch(process.env.REACT_APP_POST_LOGIN_REDIRECT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.error || 'Post-login failed');

      const cookieData = confirmData.cookieData;
      if (!cookieData?.target) throw new Error('Invalid cookie data returned');

      document.cookie = `kingdom_auth=${encodeURIComponent(JSON.stringify(cookieData))}; path=/; secure; SameSite=Lax`;
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const confirmRes = await fetch(process.env.REACT_APP_CONFIRM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.error || 'Confirmation failed');

      setUnconfirmed(false);
      setCode('');
      alert('✅ Your email has been confirmed! Please login.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      const res = await fetch(process.env.REACT_APP_RESEND_CONFIRMATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setResendCooldown(30);
      alert('📧 A new confirmation code has been sent to your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <form className="auth-form" onSubmit={unconfirmed ? handleConfirmLogin : handleLogin}>
        {logoUrl && (
          <div className="logo-container">
            <img src={logoUrl} alt="Logo" className="logo-image" />
          </div>
        )}
        <h2 className="app-title">{appName}</h2>
        <h2>{unconfirmed ? 'Confirm Your Email' : 'Login'}</h2>
        {error && <div className="auth-error">{error}</div>}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={unconfirmed}
        />

        {!unconfirmed && (
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}

        {unconfirmed && (
          <>
            <input
              type="text"
              placeholder="6-digit code from email"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <button type="button" onClick={handleResendCode} disabled={resendCooldown > 0 || loading}>
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </>
        )}

        <button type="submit" disabled={loading}>
          {loading ? 'Processing...' : unconfirmed ? 'Confirm' : 'Login'}
        </button>

        {!unconfirmed && (
          <>
            <p className="auth-link">
              Don’t have an account? <span onClick={switchToSignup}>Sign up</span>
            </p>
            <p className="auth-link">
              Forgot your password? <span onClick={switchToForgot}>Reset password</span>
            </p>
          </>
        )}
      </form>
    </div>
  );
};

export default LoginForm;
